import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Heart, User, Mail, Send, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { shareReport } from '../../api/reports';
import { InputField } from '../../components/InputField';
import { Button } from '../../components/Button';
export function ShareReport() {
  const {
    sessionId
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token,
    user
  } = useAuth();
  // State from previous screen
  const {
    avgBpm,
    minBpm,
    maxBpm,
    totalReadings
  } = location.state || {
    avgBpm: '--',
    minBpm: '--',
    maxBpm: '--',
    totalReadings: '--'
  };
  const [doctorName, setDoctorName] = useState('');
  const [doctorEmail, setDoctorEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const handleSend = async () => {
    setError(null);
    setEmailError(null);
    if (!doctorEmail || !/^\S+@\S+\.\S+$/.test(doctorEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        doctorEmail,
        doctorName: doctorName || 'Doctor',
        patientName: user?.fullName || 'Patient',
        dateRange: new Date().toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        summary: JSON.stringify({
          avgBpm,
          minBpm,
          maxBpm,
          totalSessions: 1,
          totalReadings,
          notes
        })
      };
      await shareReport(token!, payload);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send report');
    } finally {
      setIsLoading(false);
    }
  };
  const resetForm = () => {
    setDoctorName('');
    setDoctorEmail('');
    setNotes('');
    setIsSuccess(false);
    setError(null);
  };
  if (isSuccess) {
    return <main className="w-full h-full flex flex-col items-center justify-center py-12 animate-fadeInUp" data-id="element-1182">
        <motion.div className="w-[80px] h-[80px] bg-accent rounded-full flex items-center justify-center shadow-elevated mb-6" initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        bounce: 0.6,
        duration: 0.8
      }} data-id="element-1183">
          <motion.div initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          delay: 0.4,
          duration: 0.4
        }} data-id="element-1184">
            <Check size={32} className="text-white" strokeWidth={3} data-id="element-1185" />
          </motion.div>
        </motion.div>

        <h1 className="text-[24px] font-semibold text-text-primary mb-2" data-id="element-1186">
          Report sent!
        </h1>
        <p className="text-[15px] text-text-secondary text-center max-w-[300px] mb-10" data-id="element-1187">
          Your report has been sent to {doctorEmail}. Your doctor will receive
          it shortly.
        </p>

        <div className="w-full max-w-[320px] space-y-4" data-id="element-1188">
          <Button onClick={resetForm} variant="secondary" data-id="element-1189">
            Send Another Report
          </Button>
          <Button onClick={() => navigate(`/app/history/${sessionId}`)} data-id="element-1190">
            Back to Session
          </Button>
        </div>
      </main>;
  }
  return <main className="w-full h-full flex flex-col pb-8 max-w-[600px] mx-auto" data-id="element-1191">
      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 text-danger text-[14px] font-medium text-center animate-slideDown" data-id="element-1192">
          {error}
        </div>}

      {/* Header */}
      <div className="flex items-center mb-6" data-id="element-1193">
        <button onClick={() => navigate(`/app/history/${sessionId}`)} className="mr-4 text-text-secondary hover:text-text-primary transition-colors" aria-label="Go back" data-id="element-1194">
          <ArrowLeft size={24} data-id="element-1195" />
        </button>
        <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1196">
          Share with Doctor
        </h1>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-[20px] shadow-sm p-5 mb-8" data-id="element-1197">
        <div className="flex justify-between items-center mb-4" data-id="element-1198">
          <div className="flex items-center" data-id="element-1199">
            <div className="w-[24px] h-[24px] bg-primary rounded-full flex items-center justify-center mr-2" data-id="element-1200">
              <Heart size={12} className="text-white fill-white" data-id="element-1201" />
            </div>
            <span className="text-[14px] font-semibold text-text-primary tracking-[1px] uppercase" data-id="element-1202">
              Aurora Care
            </span>
          </div>
          <span className="text-[13px] text-text-secondary" data-id="element-1203">
            Monitoring Report
          </span>
        </div>

        <div className="w-full h-[1px] bg-border mb-4" data-id="element-1204" />

        <div className="space-y-2 mb-4" data-id="element-1205">
          <p className="text-[14px] text-text-primary" data-id="element-1206">
            <span className="font-medium" data-id="element-1207">Patient:</span>{' '}
            {user?.fullName || 'Patient'}
          </p>
          <p className="text-[14px] text-text-primary" data-id="element-1208">
            <span className="font-medium" data-id="element-1209">Date:</span>{' '}
            {new Date().toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
          </p>
        </div>

        <div className="bg-background rounded-[12px] p-4 mb-4 flex justify-between" data-id="element-1210">
          <div className="flex flex-col items-center" data-id="element-1211">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1212">
              Avg BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1213">
              {avgBpm}
            </span>
          </div>
          <div className="w-[1px] bg-border" data-id="element-1214" />
          <div className="flex flex-col items-center" data-id="element-1215">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1216">
              Min BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1217">
              {minBpm}
            </span>
          </div>
          <div className="w-[1px] bg-border" data-id="element-1218" />
          <div className="flex flex-col items-center" data-id="element-1219">
            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1220">
              Max BPM
            </span>
            <span className="text-[16px] font-semibold text-text-primary" data-id="element-1221">
              {maxBpm}
            </span>
          </div>
        </div>

        {notes && <div className="mb-4" data-id="element-1222">
            <p className="text-[13px] font-medium text-text-primary mb-1" data-id="element-1223">
              Notes:
            </p>
            <p className="text-[13px] text-text-secondary italic bg-background p-3 rounded-[8px]" data-id="element-1224">
              {notes}
            </p>
          </div>}

        <p className="text-[11px] text-text-secondary italic text-center mt-6" data-id="element-1225">
          Generated by Aurora Care Fetal Monitoring
        </p>
      </div>

      {/* Form */}
      <div className="space-y-5" data-id="element-1226">
        <div data-id="element-1227">
          <label htmlFor="doctorName" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1228">
            Doctor's Name
          </label>
          <InputField id="doctorName" placeholder="Dr. Amaka Osei" icon={<User size={20} data-id="element-1230" />} value={doctorName} onChange={e => setDoctorName(e.target.value)} data-id="element-1229" />
        </div>

        <div data-id="element-1231">
          <label htmlFor="doctorEmail" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1232">
            Doctor's Email
          </label>
          <InputField id="doctorEmail" type="email" placeholder="doctor@hospital.com" icon={<Mail size={20} data-id="element-1234" />} value={doctorEmail} onChange={e => {
          setDoctorEmail(e.target.value);
          if (emailError) setEmailError(null);
        }} error={emailError || undefined} data-id="element-1233" />
        </div>

        <div data-id="element-1235">
          <label htmlFor="notes" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1236">
            Add a note for your doctor (optional)
          </label>
          <textarea id="notes" className="w-full h-[100px] rounded-[12px] border border-border bg-white text-text-primary placeholder:text-text-secondary transition-all duration-300 outline-none focus:border-primary focus:shadow-focus p-4 resize-y" placeholder="e.g. Baby was very active this morning..." value={notes} onChange={e => {
          if (e.target.value.length <= 500) setNotes(e.target.value);
        }} data-id="element-1237" />
          <div className={`text-right text-[12px] mt-1 ${notes.length >= 500 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-1238">
            {notes.length} / 500 characters
          </div>
        </div>

        <div className="pt-4" data-id="element-1239">
          <Button onClick={handleSend} isLoading={isLoading} loadingText="Sending..." data-id="element-1240">
            <div className="flex items-center justify-center space-x-2" data-id="element-1241">
              <Send size={18} data-id="element-1242" />
              <span data-id="element-1243">Send Report</span>
            </div>
          </Button>
        </div>
      </div>
    </main>;
}